import React, { useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import OutboundTable from "./OutboundTable";
import OutboundTableSkeleton from "./OutboundTableSkeleton";
import BillingTotalsModal, {
  BILLING_TOTALS_PARAMS,
} from "../billing/BillingTotalsModal";
import BillingLineItemsExportModal, {
  BILLING_LINE_ITEMS_EXPORT_PARAMS,
} from "../billing/BillingLineItemsExportModal";
import { SIMPLE_INVENTORY_REQUESTS_QUERY } from "../../../constants/graphql/queries";
import { useProject } from "../../project/ProjectProvider";
import { extractBillingTypes } from "../../../utils/billingHelpers";
import useRoles from "../../../hooks/user/useRoles";
import useProjectMovewareLink from "src/hooks/useProjectMovewareLink";

const limit = 50;
const billingTotalsParams = Object.values(BILLING_TOTALS_PARAMS);
const billingLineItemsExportParams = Object.values(BILLING_LINE_ITEMS_EXPORT_PARAMS);

export default function Outbound({ title }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState(null);
  const { project } = useProject();
  const showMovewareDetails = useProjectMovewareLink(projectId);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const canViewBilling = isAdminOrManager && project?.billing;
  const showStartDate = project?.fieldSettings?.order_start_date;
  const showDeliveryDate = project?.fieldSettings?.order_delivery_date;
  const showOrderComments = project?.fieldSettings?.order_comments;
  const showOutboundWeight =
    extractBillingTypes(project?.billingTypes?.outbound, project?.billingType)?.types?.includes(
      "Hundredweight"
    ) || false;

  const { loading, data: { inventoryRequests = {} } = {}, refetch } = useQuery(
    SIMPLE_INVENTORY_REQUESTS_QUERY,
    {
      variables: {
        projectId: parseInt(projectId),
        page,
        limit,
        search,
        sortBy: [{ field: "requested_at", sortDirection: "desc" }],
      },
    }
  );

  const list = inventoryRequests?.collection || [];
  const { currentPage, totalPages, totalCount } = inventoryRequests?.metadata || {};

  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    const dialogContent = document.querySelector(".MuiDialogContent-root");
    if (dialogContent) {
      dialogContent.scrollTo({ top: 0, left: 0 });
    }
  }, []);

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setPage(1);
  }, [searchValue]);

  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleSearchChange = useCallback(e => {
    setSearchValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setSearchValue("");
    setSearch("");
    setPage(1);
  }, []);

  const handleOpenBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_TOTALS_PARAMS.open, "true");
    params.set(BILLING_TOTALS_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleOpenExportModal = () => {
    const params = new URLSearchParams(location.search);
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.open, "true");
    params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.step, "0");
    navigate({ search: params.toString() });
    setMoreOptionsAnchorEl(null);
  };

  const handleCloseBillingTotals = () => {
    const params = new URLSearchParams(location.search);
    billingTotalsParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const handleCloseExportModal = () => {
    const params = new URLSearchParams(location.search);
    billingLineItemsExportParams.forEach(param => params.delete(param));
    navigate({ search: params.toString() });
  };

  const headerAction = (
    <>
      <Tooltip title="More Options">
        <Button
          size="small"
          color="inherit"
          aria-label="Outbound more options"
          onClick={event => setMoreOptionsAnchorEl(event.currentTarget)}
          endIcon={<MoreVertIcon fontSize="small" />}
        >
          More Options
        </Button>
      </Tooltip>
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={() => setMoreOptionsAnchorEl(null)}
      >
        <MenuItem onClick={handleOpenBillingTotals}>
          Outbound Billing Totals
        </MenuItem>
        <MenuItem onClick={handleOpenExportModal}>
          Itemized Billing Excel Export
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1}
      >
        {title ? title : <Typography fontWeight={500}>Outbound Orders</Typography>}
        <TextField
          value={searchValue}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          size="small"
          placeholder="Search"
          InputProps={{
            endAdornment: searchValue ? (
              <IconButton onClick={handleClear} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton onClick={handleSearch}>
                <SearchIcon color="action" />
              </IconButton>
            ),
          }}
        />
      </Box>
      {loading ? (
        <Box px={2} pb={3}>
          <OutboundTableSkeleton />
        </Box>
      ) : (
        <Box px={2} pb={3}>
          <OutboundTable
            list={list}
            refetch={refetch}
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            showStartDate={showStartDate}
            showDeliveryDate={showDeliveryDate}
            showOrderComments={showOrderComments}
            showOutboundWeight={showOutboundWeight}
            showBillingCharge={canViewBilling}
            headerAction={canViewBilling ? headerAction : null}
            compactActions
            showMovewareDetails={showMovewareDetails}
          />
        </Box>
      )}
      {canViewBilling && queryParams.get(BILLING_TOTALS_PARAMS.open) === "true" && (
        <BillingTotalsModal
          projectId={projectId}
          onClose={handleCloseBillingTotals}
        />
      )}
      {canViewBilling && queryParams.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.open) === "true" && (
        <BillingLineItemsExportModal
          projectId={projectId}
          onClose={handleCloseExportModal}
        />
      )}
    </Paper>
  );
}
